import { useEffect, useState } from "react";

/**
 * Track an anchor element's viewport rect while `open` is true.
 *
 * Powers "portal to document.body + position: fixed" popovers so they
 * escape their parent stacking context (sidebar, modal, sticky headers)
 * and stay glued to the anchor as the user scrolls or resizes.
 *
 * Returns `null` when closed so callers can `if (!rect) return null` at
 * the top of their render — avoids stale positioning flashes on close.
 *
 * Usage:
 *   const anchorRef = useRef(null);
 *   const rect = usePortalAnchorRect(open, anchorRef);
 *   return createPortal(
 *     <div style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, ... }} />,
 *     document.body,
 *   );
 */
export function usePortalAnchorRect(open, anchorRef) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current) {
      setRect(null);
      return undefined;
    }
    const update = () => {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    };
    update();
    // rAF-throttled scroll + resize listeners so the popover tracks the
    // anchor smoothly during page scroll without hammering re-renders.
    let raf = null;
    const throttled = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; update(); });
    };
    window.addEventListener("scroll", throttled, true);
    window.addEventListener("resize", throttled);
    return () => {
      window.removeEventListener("scroll", throttled, true);
      window.removeEventListener("resize", throttled);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open, anchorRef]);

  return rect;
}
